import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Truck, CheckCircle2, ArrowRight, ChevronRight, Phone, Calculator as CalculatorIcon } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceCity, getServiceProduct } from '../data/cityMaterialContent';
import { JsonLd, useSEO } from '../utils/seo';
import { NotFound } from './NotFound';
// Delivery tiers collapsed to three rings: inner (former zones 1+2 → +$80),
// middle (zone 3 → +$120, unchanged), outer (former zones 4+5 → +$175).
// Curated city.zone values (1–5) still map cleanly onto the right tier fee.
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
function ProjectFitForMaterial(material: string): string[] {
  const m = material.toLowerCase();
  if (m.includes('topsoil') || m.includes('compost')) return ['New lawn install or overseeding', 'Raised beds and garden plots', 'Leveling low spots in the yard'];
  if (m.includes('bark') || m.includes('mulch')) return ['Refreshing flower beds and tree rings', 'Pathways and play areas', 'Reducing weeds and retaining moisture'];
  if (m.includes('drain')) return ['French drains and footing drains', 'Behind retaining walls', 'Crushed rock alternatives where drainage matters'];
  if (m.includes('minus') || m.includes('crushed')) return ['Driveways and parking pads', 'Compacted base for patios and walkways', 'General-purpose road base'];
  if (m.includes('pea gravel')) return ['Patios, walkways, and dog runs', 'Pool decks and play areas', 'Decorative groundcover'];
  return ['Driveway base and resurfacing', 'General landscape and grading', 'Filling and grading project sites'];
}
// Tabbed "Explore" panel — replaces what used to be two stacked grid
// sections on the page. Renders the same links (SEO unchanged) in one
// shorter block that lets users switch between "other materials in this
// city" and "this material in other cities" with a single tap.
function ExploreBlock({
  city,
  product,
  otherMaterials,
  otherCities
}: {
  city: any;
  product: any;
  otherMaterials: {
    product: any;
    category: any;
  }[];
  otherCities: any[];
}) {
  const [tab, setTab] = useState<'materials' | 'cities'>('materials');
  return <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2092" data-mp="1m0oze8hcflzm">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6" data-id="element-2093" data-mp="1h488x7mo8vjd">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2094" data-mp="1cacfmihcrwuj">
          Explore
        </h2>
        <div className="inline-flex rounded-xl bg-stone-100 dark:bg-white/[0.04] p-1 border border-stone-200 dark:border-white/10" role="tablist" data-id="element-2095" data-mp="12dr9tuxiehpn">
          <button type="button" role="tab" aria-selected={tab === 'materials'} onClick={() => setTab('materials')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'materials' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2096" data-mp="yauldnlafrds">
            Other materials in {city.name}
          </button>
          <button type="button" role="tab" aria-selected={tab === 'cities'} onClick={() => setTab('cities')} className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'cities' ? 'bg-white dark:bg-white/10 text-brand-orange shadow-sm' : 'text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-2097" data-mp="3e1mbzj6anjrd">
            {product.name} in other cities
          </button>
        </div>
      </div>

      {tab === 'materials' ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2098" data-mp="301v3tz2gkxla">
          {otherMaterials.map(({
        product: p
      }) => <Link key={p.slug} to={`/gravel-delivery/${city.slug}/${p.slug}`} className="group p-5 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2099" data-mp="22kwfirroa6ay">
              <div className="flex items-start justify-between gap-3" data-id="element-2100" data-mp="e07byamv18fv">
                <div data-id="element-2101" data-mp="94i7ptzwb3d8">
                  <h3 className="font-heading text-base font-bold text-stone-900 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2102" data-mp="951qte1a7xsu">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-xs text-stone-500 dark:text-stone-400" data-id="element-2103" data-mp="i2lv6vufya7o">
                    ${p.pricePerYard}/yd · Delivered to {city.name}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-brand-orange group-hover:translate-x-0.5 transition-all shrink-0" data-id="element-2104" data-mp="rtghg07ot9t4" />
              </div>
            </Link>)}
        </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-2105" data-mp="11se40xljyt9h">
          {otherCities.map(c => <Link key={c.slug} to={`/gravel-delivery/${c.slug}/${product.slug}`} className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2106" data-mp="37lcojfrfvtjf">
              <span className="text-sm font-bold text-stone-800 dark:text-white group-hover:text-brand-orange transition-colors" data-id="element-2107" data-mp="2xfuqzz2furox">
                {c.name}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-stone-400 group-hover:text-brand-orange transition-colors" data-id="element-2108" data-mp="2oo2frm5lbeja" />
            </Link>)}
        </div>}
    </section>;
}
export function CityMaterial() {
  const {
    citySlug = '',
    materialSlug = ''
  } = useParams<{
    citySlug: string;
    materialSlug: string;
  }>();
  const city = getServiceCity(citySlug);
  // Only the curated featured materials get a city landing page. Without this
  // guard every one of the 28 products paired with every city, generating
  // ~364 thin, near-duplicate pages that competed with the real /shop/:slug
  // product pages and burned crawl budget. Anything outside the curated set
  // now resolves to the 404 page instead of rendering indexable filler.
  const isFeaturedMaterial = SERVICE_PRODUCT_SLUGS.includes(materialSlug);
  const productEntry = isFeaturedMaterial ? getServiceProduct(materialSlug) : null;
  const seoProduct = productEntry?.product;
  useSEO({
    title: city && seoProduct ? `${seoProduct.name} Delivery in ${city.name}, WA | 206Gravel` : 'Gravel Delivery Service Areas | 206Gravel',
    description: city && seoProduct ? `${city.blurb} Order ${seoProduct.name.toLowerCase()} by the cubic yard with same-day or next-day delivery to ${city.name}, Washington.` : 'Browse bulk gravel, rock, soil, bark, and aggregate delivery across Seattle, King County, and Pierce County.',
    image: seoProduct?.image,
    path: city && seoProduct ? `/gravel-delivery/${city.slug}/${seoProduct.slug}` : '/service-areas'
  });
  if (!city || !productEntry) return <NotFound data-id="element-2109" data-mp="1409tudcqqyem" />;
  const {
    product,
    category
  } = productEntry;
  const zoneFee = ZONE_FEE[city.zone] || 'Custom Quote';
  const projectFits = ProjectFitForMaterial(product.name);
  // Other materials in this city — exclude the current one.
  const otherMaterials = SERVICE_PRODUCT_SLUGS.filter(s => s !== materialSlug).map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  // Other cities for this material — exclude the current one.
  const otherCities = SERVICE_CITIES.filter(c => c.slug !== city.slug);
  // Build the estimator deep link — preselects address, material, and the
  // standard full-load yardage so the user lands on a ready-to-quote form.
  const estimatorHref = `/?address=${encodeURIComponent(city.geocodeHint)}&material=${encodeURIComponent(product.name)}&qty=12#estimator`;
  const heroTitle = `${product.name} Delivery in ${city.name}, ${city.state}`;
  const description = `${city.blurb} Order ${product.name.toLowerCase()} by the cubic yard with same-day or next-day delivery to ${city.name}, Washington.`;
  const path = `/gravel-delivery/${city.slug}/${product.slug}`;
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2110" data-mp="2yh8nef4sihhd">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${product.name} delivery in ${city.name}, WA`,
      description,
      image: product.image,
      category: category.name,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.pricePerYard,
        url: `https://206gravel.com${path}`,
        availability: 'https://schema.org/InStock'
      }
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Service Areas',
        item: 'https://206gravel.com/service-areas'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: `${product.name} in ${city.name}`,
        item: `https://206gravel.com${path}`
      }]
    }]} data-id="element-2111" data-mp="2ugez3knfr0ny" />
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2112" data-mp="cac69ylp4gxs">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2113" data-mp="3uolh0pacphsk">
          <li data-id="element-2114" data-mp="2ksmmqq5sj1v">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2115" data-mp="2kj5lsn3dm8b">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2116" data-mp="qbommb4rqkk7" />
          <li data-id="element-2117" data-mp="q8kckx4mgulm">
            <Link to="/service-areas" className="hover:text-brand-orange transition-colors" data-id="element-2118" data-mp="v97y53a30j8g">
              Service Areas
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2119" data-mp="h972q3cl640d" />
          <li data-id="element-2120" data-mp="m8jlod0c6qxo">
            <Link to={`/service-areas#${city.slug}`} className="hover:text-brand-orange transition-colors" data-id="element-2121" data-mp="m80fftk1tqzh">
              {city.name}
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2122" data-mp="36z0uxxiwi4f2" />
          <li className="text-stone-900 dark:text-white font-semibold truncate" aria-current="page" data-id="element-2123" data-mp="3702aae6y9d2l">
            {product.name}
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12" data-id="element-2124" data-mp="286g4bz388an9">
        <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-2125" data-mp="1uf1h1g2adnae">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-2126" data-mp="1ucp4xjj7m759">
            <div className="inline-flex items-center gap-2 mb-3" data-id="element-2127" data-mp="1kfao3itdjgcf">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2128" data-mp="1pgrlh4cngko9" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2129" data-mp="1bg7329ancqva">
                {city.name}, {city.state} · {city.county} County · Zone{' '}
                {city.zone}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2130" data-mp="35v9onvwkrosx">
              {heroTitle}
            </h1>
            <p className="mt-4 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2131" data-mp="3kxrmt7znp175">
              {city.blurb} Order {product.name.toLowerCase()} by the cubic yard
              with same-day or next-day delivery to {city.name} and surrounding
              neighborhoods including{' '}
              {city.neighborhoods.slice(0, 3).join(', ')}, and{' '}
              {city.neighborhoods[city.neighborhoods.length - 1]}.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2132" data-mp="7jwnm9bt9sf5">
              <Link to={estimatorHref} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors shadow-lg shadow-brand-orange/20" data-id="element-2133" data-mp="2ih0mbq2pbu4">
                <CalculatorIcon className="w-4 h-4" data-id="element-2134" data-mp="cfbzivq9de8x" />
                Get an Instant Estimate
                <ArrowRight className="w-4 h-4" data-id="element-2135" data-mp="2ic7menui6rg2" />
              </Link>
              <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-stone-300 dark:border-white/15 text-stone-900 dark:text-white font-bold text-sm hover:bg-stone-100 dark:hover:bg-white/5 transition-colors" data-id="element-2136" data-mp="2e9uoumpwtzud">
                <Phone className="w-4 h-4" data-id="element-2137" data-mp="r6cuxhses584" />
                (206) 619-3742
              </a>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-xs" data-id="element-2138" data-mp="w5fz700ke7f1">
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2139" data-mp="w2tj2bzlyjl7">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2140" data-mp="1552poqrr9stb">
                  Price
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2141" data-mp="1evnwv2lhw5nu">
                  ${product.pricePerYard}/yd
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2142" data-mp="1jwblxiq5ycff">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2143" data-mp="1ntzj6sfbwhah">
                  Delivery
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2144" data-mp="34b84a7in81bs">
                  {zoneFee}
                </div>
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2145" data-mp="2uev7v1vpr9dc">
                <div className="text-[10px] uppercase tracking-wider text-stone-500 dark:text-stone-400 font-bold mb-0.5" data-id="element-2146" data-mp="3s1c5k88czozm">
                  Lead Time
                </div>
                <div className="font-bold text-stone-900 dark:text-white" data-id="element-2147" data-mp="3ican8beccvih">
                  Same-day*
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative rounded-2xl overflow-hidden border border-stone-200 dark:border-white/10 bg-white dark:bg-white/[0.04] aspect-[4/3]" data-id="element-2148" data-mp="enh6raofei85">
            {product.image ? <img src={product.image} alt={`${product.name} delivered in ${city.name}, WA`} className="w-full h-full object-cover" loading="eager" data-id="element-2149" data-mp="3ujm8tq8okr7k" /> : <div className="w-full h-full bg-stone-100 dark:bg-stone-800" data-id="element-2150" data-mp="3lkipcl5lq9sj" />}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between" data-id="element-2151" data-mp="d58sbrgkjurm">
              <span className="px-3 py-1 rounded-full bg-black/70 backdrop-blur-md text-white text-[11px] font-bold" data-id="element-2152" data-mp="d7laa4mbu54o">
                {category.name}
              </span>
              <Link to={`/shop/${product.slug}`} className="px-3 py-1 rounded-full bg-brand-orange text-white text-[11px] font-bold hover:bg-brand-orange-hover transition-colors" data-id="element-2153" data-mp="3f5p6yfqh0fk">
                View Material
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What it's for */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2154" data-mp="10wmibqr8ze9f">
        <div className="grid md:grid-cols-3 gap-8" data-id="element-2155" data-mp="2gmni6m64v2ht">
          <div className="md:col-span-1" data-id="element-2156" data-mp="2glvm7a8mj2r0">
            <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2157" data-mp="2udae66kieea7">
              What {city.name} customers use{' '}
              <span className="text-brand-orange" data-id="element-2158" data-mp="2qe1njakrmwiv">{product.name}</span> for
            </h2>
            <p className="mt-3 text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2159" data-mp="22mmg53e4tp0r">
              {product.description}
            </p>
          </div>
          <div className="md:col-span-2" data-id="element-2160" data-mp="2bnauy0bcntep">
            <ul className="space-y-3" data-id="element-2161" data-mp="2boc582kjkbvd">
              {projectFits.map(fit => <li key={fit} className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2162" data-mp="26mwkkbma07di">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2163" data-mp="3i34tcusawexm" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2164" data-mp="3i5h7ewuxu55e">
                    {fit}
                  </span>
                </li>)}
              {product.bestFor && product.bestFor.length > 0 && <li className="flex items-start gap-3 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2165" data-mp="2oqdfp119gx0x">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-2166" data-mp="2to6mnwae1v29" />
                  <span className="text-sm text-stone-800 dark:text-stone-200 font-medium" data-id="element-2167" data-mp="1x08oojizxbqq">
                    Also great for: {product.bestFor.join(', ')}
                  </span>
                </li>}
            </ul>
          </div>
        </div>
      </section>

      {/* Delivery to this city */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-stone-200 dark:border-white/10" data-id="element-2168" data-mp="2arnjroq6h3e5">
        <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight mb-6" data-id="element-2169" data-mp="1daosq9o0by04">
          Delivering to {city.name}, {city.state}
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-id="element-2170" data-mp="1ar1u8j7ux3gp">
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2171" data-mp="1aofrcwvwvc4t">
            <div className="flex items-center gap-3 mb-3" data-id="element-2172" data-mp="10sceox3fnv3m">
              <div className="w-10 h-10 rounded-full bg-brand-orange/10 flex items-center justify-center" data-id="element-2173" data-mp="10qsy15dv7i1b">
                <Truck className="w-5 h-5 text-brand-orange" data-id="element-2174" data-mp="wt3illkx7l4w" />
              </div>
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white" data-id="element-2175" data-mp="rtijnb5r3cs6">
                Zone {city.zone} — {zoneFee} delivery
              </h3>
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2176" data-mp="i2wbu349zqk1">
              {city.name} sits in our Zone {city.zone} delivery area. Our
              standard {city.zone === 1 ? 'inner-zone' : 'core-zone'} rate
              applies — billed per truckload, not per yard.
            </p>
            <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-2177" data-mp="42v9ej8d4b2g">
              See {city.name} on the zone map
              <ArrowRight className="w-3 h-3" data-id="element-2178" data-mp="1iyd26fmx8rzs" />
            </Link>
          </div>
          <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10" data-id="element-2179" data-mp="1j0fqmv8fed2e">
            <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-2180" data-mp="198jtpni2sszr">
              Neighborhoods we cover
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-2181" data-mp="1e01qs0gza9fg">
              {city.neighborhoods.map(n => <span key={n} className="px-3 py-1 rounded-full bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-xs font-medium text-stone-700 dark:text-stone-300" data-id="element-2182" data-mp="zbf3ncl40ddo">
                  {n}
                </span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Explore — single tabbed block that combines "other materials" and
                    "other cities" so the page doesn't feel like a sitemap of stacked
                    identical link grids. Same SEO links, half the scroll. */}
      <ExploreBlock city={city} product={product} otherMaterials={otherMaterials} otherCities={otherCities} data-id="element-2183" data-mp="6q0a63zc4gn5" />

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-stone-200 dark:border-white/10" data-id="element-2184" data-mp="gmbpz3y4vomc">
        <div className="p-8 md:p-10 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 dark:border-white/10 text-center" data-id="element-2185" data-mp="ukt6aglplyu8">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-2186" data-mp="pmhysciw00f6">
            Ready to schedule {product.name} delivery to {city.name}?
          </h2>
          <p className="mt-3 text-stone-300 max-w-2xl mx-auto text-sm md:text-base" data-id="element-2187" data-mp="zd33mhp5m16j">
            Get an instant estimate — no signup, no calls required. Or call
            dispatch directly to skip the form.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3" data-id="element-2188" data-mp="18b6hqsxt7vob">
            <Link to={estimatorHref} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2189" data-mp="1ku68onrftdd5">
              <CalculatorIcon className="w-4 h-4" data-id="element-2190" data-mp="1utner6alpt9n" />
              Get Instant Estimate
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-2191" data-mp="1pvvjc2aqzp0p">
              <Phone className="w-4 h-4" data-id="element-2192" data-mp="x7pwna04vpy8" />
              (206) 619-3742
            </a>
          </div>
          <p className="mt-5 text-[11px] text-stone-500" data-id="element-2193" data-mp="1bwvw8z2vvi6n">
            *Same-day delivery subject to truck availability. Standard hours
            Mon–Sat.
          </p>
        </div>
      </section>
    </main>;
}